import React, { forwardRef, useRef, useCallback, PropsWithChildren } from 'react';
import PropTypes from 'prop-types';
// @ts-ignore
import { Button } from '@pwc/appkit-react/lib/Button';

type BaseFileUploadButtonParams = Omit<React.HTMLProps<HTMLInputElement>,
  'accept' | 'children' | 'onChange'
  >;
type RequiredBaseFileUploadButtonParams = Required<Pick<React.HTMLProps<HTMLInputElement>,
  'accept' | 'children' | 'onChange'
  >>;
type FileUploadButtonParams = BaseFileUploadButtonParams & RequiredBaseFileUploadButtonParams & {
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  forwardedRef?: React.MutableRefObject<HTMLInputElement>,
  gray?: boolean,
  negative?: boolean,
  isLoading?: boolean,
};

// This component uses hidden file input (because it's hard/impossible to style it)
// and upload button (easy to style) to handle file selection
const FileUploadButton = ({
  accept,
  children,
  disabled = false,
  onChange,
  onClick,
  className,
  forwardedRef,
  kind,
  gray,
  negative,
  isLoading,
}: FileUploadButtonParams) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openFileSelect = useCallback(
    () => forwardedRef ? forwardedRef.current.click() : inputRef.current?.click(),
    [forwardedRef]
  );

  return (
    <>
      <input
        ref={forwardedRef || inputRef}
        type="file"
        name="file-button"
        tabIndex={-1}
        onChange={onChange}
        onClick={onClick}
        disabled={disabled}
        accept={accept}
        style={{
          position: 'absolute',
          height: '0',
          width: '0',
          display: 'none',
        }}
      />
      <Button
        className={className}
        kind={kind}
        type="button"
        negative={negative}
        isLoading={isLoading}
        disabled={disabled}
        gray={gray}
        onClick={openFileSelect}
      >
        {children}
      </Button>
    </>
  );
};

FileUploadButton.propTypes = {
  accept: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  forwardedRef: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  kind: PropTypes.string,
  gray: PropTypes.bool,
  negative: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default forwardRef<HTMLInputElement, FileUploadButtonParams>(function FileUploadButtonWrapper(props, ref) {
  return <FileUploadButton
    {...props as PropsWithChildren<FileUploadButtonParams>}
    forwardedRef={ref as React.MutableRefObject<HTMLInputElement>}
  />;
});
